/**
 * Temporal back stack for panel navigation.
 *
 * Panel navigation writes history entries directly (see PanelController's
 * handleSlideChange) but nothing has ever read them back, so there was no
 * record of *where the user came from* — only where they are. The Android
 * hardware back button needs that record.
 *
 * We keep our own stack rather than leaning on `window.history` because the
 * browser stack is shared with React Router, `replaceState` fixups and the
 * pre-login redirect in api.ts. Popping it blindly could navigate the WebView
 * clean out of the app, which is the one failure mode a hardware back button
 * must never have.
 *
 * Entries are keyed by panelId, never by index: panels are inserted and closed
 * around the centered one, so indices shift under us while ids do not. That
 * also makes the stack immune to desktop/mobile mode switches, which re-center
 * the same panel at a different index.
 */

export type PanelBackEntry = {
    panelId: string
    path: string
}

export type PanelBackStack = {
    entries: PanelBackEntry[]
}

/** Bounds memory on long sessions; far deeper than anyone navigates by hand. */
export const MAX_PANEL_BACK_ENTRIES = 50

export function createPanelBackStack(): PanelBackStack {
    return { entries: [] }
}

/** Record navigation immediately, even while thread content is loading. */
export function recordCenteredPanelVisit<Filter>(
    stack: PanelBackStack,
    panel: { panelId: string; filter?: Filter } | null | undefined,
    pathForFilter: (filter: Filter) => string,
): void {
    if (!panel?.panelId) return

    recordPanelVisit(stack, {
        panelId: panel.panelId,
        // Empty panels have no filter. Their identity is still enough to go back.
        path: panel.filter ? pathForFilter(panel.filter) : '',
    })
}

/**
 * Records the panel that is now centered. Re-recording the current panel
 * refreshes its path instead of stacking a duplicate, so the repeated
 * slideChange events fired by mode switches and restores cannot inflate the
 * stack — and a back-driven slide settles back onto its own entry harmlessly.
 */
export function recordPanelVisit(stack: PanelBackStack, entry: PanelBackEntry): void {
    if (!entry.panelId) return

    const current = stack.entries[stack.entries.length - 1]
    if (current && current.panelId === entry.panelId) {
        current.path = entry.path
        return
    }

    stack.entries.push({ ...entry })
    if (stack.entries.length > MAX_PANEL_BACK_ENTRIES) stack.entries.shift()
}

export function peekCurrentPanel(stack: PanelBackStack): PanelBackEntry | null {
    return stack.entries[stack.entries.length - 1] ?? null
}

/**
 * True when some earlier panel is still open and could be returned to.
 * Non-mutating counterpart of takePreviousPanel, and agrees with it exactly.
 */
export function hasPreviousPanel(
    stack: PanelBackStack,
    isPanelAvailable: (panelId: string) => boolean,
): boolean {
    for (let index = stack.entries.length - 2; index >= 0; index--) {
        if (isPanelAvailable(stack.entries[index].panelId)) return true
    }
    return false
}

/**
 * Drops the current panel and returns the most recent earlier one that is
 * still open, discarding entries for panels that have since been closed. The
 * returned entry is left on top of the stack as the new current panel.
 */
export function takePreviousPanel(
    stack: PanelBackStack,
    isPanelAvailable: (panelId: string) => boolean,
): PanelBackEntry | null {
    if (!hasPreviousPanel(stack, isPanelAvailable)) return null

    stack.entries.pop()
    while (stack.entries.length > 0) {
        const candidate = stack.entries[stack.entries.length - 1]
        if (isPanelAvailable(candidate.panelId)) return candidate
        stack.entries.pop()
    }
    return null
}

export function resetPanelBackStack(stack: PanelBackStack): void {
    stack.entries.length = 0
}

const defaultStack = createPanelBackStack()

export function getPanelBackStack(): PanelBackStack {
    return defaultStack
}
